export const TELEGRAM_START_PARAMETER_MAX_LENGTH = 512;

const PAGE_SEGMENT_PATTERN = /^[A-Za-z0-9_][A-Za-z0-9_-]*$/;
const PARAMETER_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_-]*$/;
const UNSAFE_VALUE_PATTERN = /[\u0000-\u001F\u007F#]|--/u;
const SENSITIVE_PARAMETER_NAME_PATTERN = /(?:user|phone|address|token|api|key|secret|session|auth|permission|role|grant|backend|return|redirect|password|email)/iu;

export const TELEGRAM_MINI_APP_URL = 'https://t.me/iFoodMeBot/iFoodMe';

type TelegramStartWindow = Window & {
  Telegram?: {
    WebApp?: {
      initDataUnsafe?: {
        start_param?: unknown;
      };
    };
  };
};

export type TelegramStartDestination = {
  pageSegments: readonly string[];
  parameters: ReadonlyArray<readonly [string, string]>;
};

function isSafePageSegment(value: string): boolean {
  return PAGE_SEGMENT_PATTERN.test(value);
}

function isSafeParameterName(value: string): boolean {
  return PARAMETER_NAME_PATTERN.test(value);
}

function isSafeParameterValue(value: string): boolean {
  return value.length > 0 && !UNSAFE_VALUE_PATTERN.test(value);
}

/**
 * Ensures a destination can be represented by the documented delimiter grammar
 * and can only resolve to a same-origin application pathname.
 */
function isValidDestination(destination: TelegramStartDestination): boolean {
  return destination.pageSegments.length > 0
    && destination.pageSegments.every(isSafePageSegment)
    && destination.parameters.every(([name, value]) => (
      isSafeParameterName(name) && isSafeParameterValue(value)
    ));
}

/**
 * Parses the `page`/`param` convention without allowing an invalid launch
 * payload to affect routing. Page sections must precede parameter sections.
 */
export function decodeTelegramStartParameter(parameter: unknown): TelegramStartDestination | null {
  if (
    typeof parameter !== 'string'
    || parameter.length === 0
    || parameter.length > TELEGRAM_START_PARAMETER_MAX_LENGTH
  ) {
    return null;
  }

  const pageSegments: string[] = [];
  const parameters: Array<[string, string]> = [];
  let foundParameter = false;

  for (const section of parameter.split('--')) {
    const keywordSeparator = section.indexOf('-');
    if (!section || keywordSeparator <= 0) {
      return null;
    }

    const keyword = section.slice(0, keywordSeparator);
    const payload = section.slice(keywordSeparator + 1);

    if (keyword === 'page') {
      if (foundParameter || !isSafePageSegment(payload)) {
        return null;
      }
      pageSegments.push(payload);
      continue;
    }

    if (keyword === 'param') {
      const parameterSeparator = payload.indexOf('-');
      if (pageSegments.length === 0 || parameterSeparator <= 0) {
        return null;
      }

      const name = payload.slice(0, parameterSeparator);
      const value = payload.slice(parameterSeparator + 1);
      if (!isSafeParameterName(name) || !isSafeParameterValue(value)) {
        return null;
      }

      foundParameter = true;
      parameters.push([name, value]);
      continue;
    }

    return null;
  }

  const destination: TelegramStartDestination = { pageSegments, parameters };
  return isValidDestination(destination) ? destination : null;
}

/** Serializes a validated destination using the shared `page`/`param` grammar. */
export function encodeTelegramStartParameter(destination: TelegramStartDestination): string {
  if (!isValidDestination(destination)) {
    throw new TypeError('The Telegram Mini App start destination is invalid.');
  }

  const parameter = [
    ...destination.pageSegments.map((segment) => `page-${segment}`),
    ...destination.parameters.map(([name, value]) => `param-${name}-${value}`),
  ].join('--');

  if (parameter.length > TELEGRAM_START_PARAMETER_MAX_LENGTH) {
    throw new RangeError(`The Telegram Mini App start parameter exceeds ${TELEGRAM_START_PARAMETER_MAX_LENGTH} characters.`);
  }

  return parameter;
}

/** Builds an internal route and serializes query data through URLSearchParams. */
export function buildTelegramStartRoute(destination: TelegramStartDestination): string {
  if (!isValidDestination(destination)) {
    throw new TypeError('The Telegram Mini App start destination is invalid.');
  }

  const pathname = `/${destination.pageSegments.join('/')}`;
  const internalOrigin = 'https://ifood.invalid';
  const internalUrl = new URL(pathname, internalOrigin);
  if (internalUrl.origin !== internalOrigin || internalUrl.pathname !== pathname) {
    throw new TypeError('The Telegram Mini App route must remain internal.');
  }

  const searchParameters = new URLSearchParams();
  destination.parameters.forEach(([name, value]) => searchParameters.append(name, value));
  const query = searchParameters.toString();
  return `${internalUrl.pathname}${query ? `?${query}` : ''}`;
}

export type TelegramLinkConversion =
  | { status: 'empty' }
  | { status: 'invalid'; error: string }
  | { status: 'valid'; destination: TelegramStartDestination; startParameter: string; telegramUrl: string };

/**
 * Converts a pasted internal path or approved frontend URL into the shared
 * Telegram convention. The caller supplies origin policy so this remains a
 * pure helper while application configuration owns accepted domains.
 */
export function convertIfoodUrlToTelegramLink(
  source: string,
  isAcceptedOrigin: (origin: string) => boolean,
): TelegramLinkConversion {
  const trimmedSource = source.trim();
  if (!trimmedSource) return { status: 'empty' };

  let sourceUrl: URL;
  if (trimmedSource.startsWith('/')) {
    if (trimmedSource.startsWith('//')) return { status: 'invalid', error: 'Protocol-relative URLs are not supported.' };
    try {
      sourceUrl = new URL(trimmedSource, 'https://ifood.invalid');
    } catch {
      return { status: 'invalid', error: 'Enter a valid internal path.' };
    }
  } else {
    try {
      sourceUrl = new URL(trimmedSource);
    } catch {
      return { status: 'invalid', error: 'Enter an internal path or an approved i-food URL.' };
    }
    if (sourceUrl.protocol !== 'http:' && sourceUrl.protocol !== 'https:') return { status: 'invalid', error: 'Only HTTP(S) i-food URLs are supported.' };
    if (sourceUrl.username || sourceUrl.password) return { status: 'invalid', error: 'URLs with credentials are not supported.' };
    if (!isAcceptedOrigin(sourceUrl.origin)) return { status: 'invalid', error: 'This URL origin is not an approved i-food frontend.' };
  }

  if (sourceUrl.hash) return { status: 'invalid', error: 'URL fragments cannot be represented in a Telegram link.' };

  const destination: TelegramStartDestination = {
    pageSegments: sourceUrl.pathname.split('/').filter(Boolean),
    parameters: Array.from(sourceUrl.searchParams.entries()),
  };
  if (destination.parameters.some(([name]) => SENSITIVE_PARAMETER_NAME_PATTERN.test(name))) {
    return { status: 'invalid', error: 'Sensitive URL parameters cannot be included in a Telegram link.' };
  }

  try {
    const startParameter = encodeTelegramStartParameter(destination);
    const telegramUrl = new URL(TELEGRAM_MINI_APP_URL);
    telegramUrl.searchParams.set('startapp', startParameter);
    return { status: 'valid', destination, startParameter, telegramUrl: telegramUrl.toString() };
  } catch {
    return { status: 'invalid', error: 'This path or query value cannot be represented by the current Telegram convention.' };
  }
}
/**
 * Prefers Telegram's early GET copy. The SDK fallback is used only when the
 * GET copy is absent, so malformed visible input cannot override it.
 */
export function resolveTelegramStartRoute(search: string, unsafeStartParameter?: unknown): string | null {
  const searchParameters = new URLSearchParams(search);
  if (searchParameters.has('tgWebAppStartParam')) {
    if (searchParameters.getAll('tgWebAppStartParam').length !== 1) {
      return null;
    }
    const destination = decodeTelegramStartParameter(searchParameters.get('tgWebAppStartParam'));
    return destination ? buildTelegramStartRoute(destination) : null;
  }

  const destination = decodeTelegramStartParameter(unsafeStartParameter);
  return destination ? buildTelegramStartRoute(destination) : null;
}

/**
 * Replaces the browser location before React Router mounts, retaining the
 * Telegram launch hash while consuming navigation input as an internal route.
 */
export function applyTelegramStartParameterFromWindow(): 'applied' | 'missing-or-invalid' {
  const telegramWindow = window as TelegramStartWindow;
  const route = resolveTelegramStartRoute(
    window.location.search,
    telegramWindow.Telegram?.WebApp?.initDataUnsafe?.start_param,
  );
  if (!route) {
    return 'missing-or-invalid';
  }

  const nextLocation = `${route}${window.location.hash}`;
  const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextLocation !== currentLocation) {
    window.history.replaceState(window.history.state, '', nextLocation);
  }
  return 'applied';
}